<script setup lang="ts">
import { type VariantProps, cva } from 'class-variance-authority';
import { computed } from 'vue';
import { cn } from '@/lib/utils';

defineOptions({
  inheritAttrs: false,
});

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        small: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        large: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

// Most of the app still calls <Button> with PrimeVue's API (severity/label/icon-pos/as)
// rather than this component's native shadcn API (variant/size + default slot). Rather
// than rewrite every call site, this component understands both: an explicit `variant`
// always wins, otherwise `severity` is mapped onto the closest shadcn variant so those
// (~150+) existing call sites render as intended instead of silently falling back to
// the green "default" variant.
const severityToVariant: Record<string, NonNullable<ButtonVariants['variant']>> = {
  primary: 'default',
  success: 'default',
  secondary: 'secondary',
  contrast: 'secondary',
  info: 'secondary',
  warn: 'secondary',
  warning: 'secondary',
  danger: 'destructive',
};

interface Props {
  variant?: ButtonVariants['variant'];
  severity?: string;
  size?: ButtonVariants['size'];
  /** Element/component to render as, e.g. "router-link" (PrimeVue-style `as` prop). */
  as?: string;
  label?: string;
  iconPos?: 'left' | 'right' | 'top' | 'bottom';
  tooltip?: string;
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
});

const resolvedVariant = computed<ButtonVariants['variant']>(
  () =>
    props.variant ??
    (props.severity ? (severityToVariant[props.severity] ?? 'secondary') : 'default'),
);

const computedClasses = computed(() =>
  cn(buttonVariants({ variant: resolvedVariant.value, size: props.size }), props.class),
);

const iconAfter = computed(() => props.iconPos === 'right' || props.iconPos === 'bottom');
</script>

<template>
  <component :is="as" :class="computedClasses" :title="tooltip" v-bind="$attrs">
    <slot v-if="!iconAfter" name="icon" />
    <slot>{{ label }}</slot>
    <slot v-if="iconAfter" name="icon" />
  </component>
</template>
